import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { customersAPI, crmJobsAPI, estimatesAPI, invoicesAPI, tagsAPI, i18nSettingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { getAddressConfig } from '../lib/i18n-config';
import TagMultiSelect, { TagChips } from '../components/TagMultiSelect';
import {
  ArrowLeft, Loader2, MapPin, Mail, Phone, Plus, Edit2, Trash2, Save, X,
  Building2, CheckCircle2, User, Briefcase, FileText, Receipt, MessageSquare,
  AlertTriangle, Tag, StickyNote, Activity, Shield, ExternalLink, Home,
} from 'lucide-react';
import { toast } from 'sonner';
import { JobStatusBadge, cleanJobNumber } from '../components/JobStatusBadge';
import CommunicationPreferencesCard from '../components/CommunicationPreferencesCard';
import LeadSourceDropdown from '../components/LeadSourceDropdown';
import CustomerAttachmentsCard from '../components/CustomerAttachmentsCard';
import { BillingBadge } from '../components/BillingBadge';
import CustomerActivityFeed from '../components/CustomerActivityFeed';
import CustomerMaintenancePlans from '../components/CustomerMaintenancePlans';
import CustomerNotesTabs from '../components/CustomerNotesTabs';
import CustomerMessagesTab from '../components/CustomerMessagesTab';
import { CustomerCallsTab } from '../components/CustomerCallsTab';

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function buildGoogleMapsUrl(address, latitude, longitude) {
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  if (latitude && longitude) return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  return null;
}

function CompactMapPreview({ address, latitude, longitude }) {
  const [googleFailed, setGoogleFailed] = useState(false);
  const mapsUrl = buildGoogleMapsUrl(address, latitude, longitude);
  const hasCoords = latitude && longitude;

  // Tier 1: Google Static Maps (needs enabled API + key)
  const staticQuery = address ? encodeURIComponent(address) : hasCoords ? `${latitude},${longitude}` : null;
  const googleSrc = MAPS_API_KEY && staticQuery
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${staticQuery}&zoom=15&size=400x300&scale=2&maptype=roadmap&markers=color:red|${staticQuery}&key=${MAPS_API_KEY}`
    : null;

  const showGoogle = googleSrc && !googleFailed;

  // Tier 2: OpenStreetMap embed (free, no API key, needs lat/lng)
  const osmEmbedUrl = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${(longitude - 0.008).toFixed(6)},${(latitude - 0.005).toFixed(6)},${(longitude + 0.008).toFixed(6)},${(latitude + 0.005).toFixed(6)}&layer=mapnik&marker=${latitude},${longitude}`
    : null;

  const showOsm = !showGoogle && osmEmbedUrl;

  return (
    <div data-testid="compact-map-preview" className="h-full">
      {showGoogle ? (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block h-full hover:opacity-95 transition-opacity">
          <img
            src={googleSrc}
            alt="Map preview"
            className="w-full h-full object-cover"
            onError={() => setGoogleFailed(true)}
            data-testid="static-map-img"
          />
        </a>
      ) : showOsm ? (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block h-full">
          <iframe
            src={osmEmbedUrl}
            className="w-full h-full pointer-events-none"
            title="Map preview"
            loading="lazy"
            data-testid="osm-map-embed"
          />
        </a>
      ) : (
        <div className="bg-muted/30 flex items-center justify-center h-full" data-testid="map-fallback">
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground/40">
            <MapPin className="w-5 h-5" />
            <span className="text-[10px]">Map preview unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_LOCATION = {
  label: '', address1: '', address2: '', city: '', state: '', zip: '',
  is_billing_location: false, is_service_location: false,
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [locationDrawerOpen, setLocationDrawerOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({ ...EMPTY_LOCATION });
  const [locationSaving, setLocationSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  // Tab + conversation-id are persisted in the URL so that a hard
  // reload, a tab navigation, or a deep link from a notification all
  // restore the operator to the exact view they were on. Previously
  // these were component-local state, and refreshing while inside a
  // Messages thread dumped the user back to the profile root — which
  // caused real missed-message UX during the SMS testing.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';
  const initialConv = searchParams.get('conv') || null;
  const [activeTab, setActiveTabState] = useState(initialTab);
  const setActiveTab = (next) => {
    setActiveTabState(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    // Drop the conversation pin when leaving Messages so other tabs
    // don't keep a stale `?conv=` on the URL.
    if (next !== 'messages') nextParams.delete('conv');
    setSearchParams(nextParams, { replace: true });
  };
  const setSelectedConversationId = useCallback((convId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'messages');
      if (convId) next.set('conv', convId);
      else next.delete('conv');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [allCustomerTags, setAllCustomerTags] = useState([]);
  const [businessCountry, setBusinessCountry] = useState('US');
  const [serviceCountries, setServiceCountries] = useState(['US']);
  const [planBadge, setPlanBadge] = useState(null);

  // Load tenant's business country + service-country list (multi-country support)
  useEffect(() => {
    i18nSettingsAPI.get().then(res => {
      const primary = res.data?.business_country_code || 'US';
      setBusinessCountry(primary);
      const sc = res.data?.service_country_codes;
      setServiceCountries(Array.isArray(sc) && sc.length > 0 ? sc : [primary]);
    }).catch(() => {});
  }, []);

  const fetchCustomer = useCallback(async () => {
    try {
      const res = await customersAPI.get(id);
      setCustomer(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error('Customer not found');
        navigate('/customers');
      } else {
        toast.error('Failed to load customer');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await crmJobsAPI.list({ customer_id: id, page_size: 100 });
      setJobs(res.data.jobs || []);
    } catch {
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchCustomer(); }, [fetchCustomer]);

  useEffect(() => {
    if (customer?.id) {
      customersAPI.getSummary(customer.id).then(res => {
        setPlanBadge({ name: res.data.best_plan_name, status: res.data.best_plan_status });
      }).catch(() => {});
    }
  }, [customer?.id]);

  useEffect(() => {
    tagsAPI.list({ entity_type: 'customer' }).then(res => setAllCustomerTags(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'jobs') fetchJobs();
  }, [activeTab, fetchJobs]);

  const openEditDrawer = () => {
    setEditForm({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      company_name: customer.company_name || '',
      customer_type: customer.customer_type || '',
      primary_phone: customer.primary_phone || '',
      secondary_phone: customer.secondary_phone || '',
      billing_email: customer.billing_email || '',
      billing_phone: customer.billing_phone || '',
      service_street: customer.service_street || '',
      service_unit: customer.service_unit || '',
      service_city: customer.service_city || '',
      service_state: customer.service_state || '',
      service_zip: customer.service_zip || '',
      service_address_notes: customer.service_address_notes || '',
      latitude: customer.latitude,
      longitude: customer.longitude,
      customer_notes: customer.customer_notes || '',
      tags: customer.tags || '',
      selectedTags: customer.tag_objects || [],
      lead_source: customer.lead_source || '',
      lead_source_id: customer.lead_source_id || '',
      lead_source_name: customer.lead_source_name || '',
      do_not_service: customer.do_not_service || false,
      notifications_enabled: customer.notifications_enabled !== false,
      sms_consent: customer.sms_consent || 'unknown',
      email_marketing_consent: customer.email_marketing_consent || 'unknown',
      billing_customer_id: customer.billing_customer_id || '',
      _billing_display_name: customer.billing_customer?.display_name || '',
    });
    setEditDrawerOpen(true);
  };

  const handlePlaceSelect = useCallback((place) => {
    setEditForm(prev => ({ ...prev, ...place }));
  }, []);

  const handleLocationPlaceSelect = useCallback((place) => {
    // AddressAutocomplete with fieldPrefix="" sends: street, city, state, zip
    setLocationForm(prev => ({
      ...prev,
      address1: place.street || prev.address1,
      city: place.city || prev.city,
      state: place.state || prev.state,
      zip: place.zip || prev.zip,
    }));
  }, []);

  const handleCreateTag = async (name, entityType) => {
    try {
      const res = await tagsAPI.create({ name, entity_type: entityType });
      const newTag = res.data;
      setAllCustomerTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      return newTag;
    } catch { return null; }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    if (!editForm.first_name?.trim() && !editForm.company_name?.trim()) {
      toast.error('First name or company name is required');
      return;
    }
    if (!editForm.primary_phone?.trim()) {
      toast.error('Primary phone is required');
      return;
    }
    if (!editForm.service_street?.trim()) {
      toast.error('Service street is required');
      return;
    }
    setSaving(true);
    try {
      await customersAPI.update(id, {
        first_name: editForm.first_name.trim() || null,
        last_name: editForm.last_name.trim() || null,
        company_name: editForm.company_name.trim() || null,
        customer_type: editForm.customer_type || null,
        primary_phone: editForm.primary_phone.trim(),
        secondary_phone: editForm.secondary_phone.trim() || null,
        billing_email: editForm.billing_email.trim() || null,
        billing_phone: editForm.billing_phone.trim() || null,
        service_street: editForm.service_street.trim(),
        service_unit: editForm.service_unit.trim() || null,
        service_city: editForm.service_city.trim() || null,
        service_state: editForm.service_state.trim() || null,
        service_zip: editForm.service_zip.trim() || null,
        service_address_notes: editForm.service_address_notes.trim() || null,
        latitude: editForm.latitude,
        longitude: editForm.longitude,
        customer_notes: editForm.customer_notes.trim() || null,
        tags: editForm.tags?.trim() || null,
        tag_ids: (editForm.selectedTags || []).map(t => t.id),
        lead_source: editForm.lead_source.trim() || null,
        lead_source_id: editForm.lead_source_id || null,
        do_not_service: editForm.do_not_service,
        notifications_enabled: editForm.notifications_enabled,
        sms_consent: editForm.sms_consent,
        email_marketing_consent: editForm.email_marketing_consent,
        billing_customer_id: editForm.billing_customer_id || '',
      });
      toast.success('Customer updated');
      setEditDrawerOpen(false);
      fetchCustomer();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await customersAPI.remove(id);
      toast.success('Customer deleted');
      navigate('/customers');
    } catch { toast.error('Failed to delete customer'); }
  };

  const openLocationDrawer = (loc = null) => {
    if (loc) {
      setEditingLocation(loc);
      setLocationForm({
        label: loc.label || '', address1: loc.address1 || '', address2: loc.address2 || '',
        city: loc.city || '', state: loc.state || '', zip: loc.zip || '',
        is_billing_location: loc.is_billing_location, is_service_location: loc.is_service_location,
      });
    } else {
      setEditingLocation(null);
      setLocationForm({ ...EMPTY_LOCATION });
    }
    setLocationDrawerOpen(true);
  };

  const handleSaveLocation = async (e) => {
    e.preventDefault();
    setLocationSaving(true);
    try {
      if (editingLocation) {
        await customersAPI.updateLocation(id, editingLocation.id, locationForm);
        toast.success('Location updated');
      } else {
        await customersAPI.createLocation(id, locationForm);
        toast.success('Location added');
      }
      setLocationDrawerOpen(false);
      fetchCustomer();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save location');
    } finally { setLocationSaving(false); }
  };

  const handleDeleteLocation = async (locId) => {
    try {
      await customersAPI.removeLocation(id, locId);
      toast.success('Location deleted');
      setDeleteConfirm(null);
      fetchCustomer();
    } catch { toast.error('Failed to delete location'); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!customer) return null;

  const serviceAddress = [customer.service_street, customer.service_city, customer.service_state, customer.service_zip].filter(Boolean).join(', ');

  return (
    <Layout>
      <div className="space-y-6" data-testid="customer-detail-page">
        {/* Back button */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/customers')} data-testid="back-to-customers">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        {/* Customer Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary shrink-0">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate" data-testid="customer-name">{customer.display_name}</h1>
                {canEdit && (
                  <button
                    onClick={openEditDrawer}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors"
                    title="Edit"
                    data-testid="edit-customer-btn"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={() => setDeleteConfirm('customer')}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10 transition-colors"
                    title="Delete"
                    data-testid="delete-customer-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {!customer.is_active && <Badge variant="secondary">Inactive</Badge>}
                {customer.do_not_service && (
                  <Badge variant="destructive" className="flex items-center gap-1" data-testid="dns-badge">
                    <AlertTriangle className="w-3 h-3" /> DNS
                  </Badge>
                )}
                <MaintenancePlanBadge planName={planBadge?.name} planStatus={planBadge?.status} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
                {customer.customer_type && <span className="capitalize">{customer.customer_type}</span>}
                {customer.primary_phone && (
                  <button
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('softphone:dial', {
                        detail: { number: customer.primary_phone, customerId: customer.id, customerName: customer.display_name }
                      }));
                    }}
                    className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
                    title="Click to call"
                    data-testid="header-phone-click-to-call"
                  >
                    <Phone className="w-3.5 h-3.5" /> {customer.primary_phone}
                  </button>
                )}
                {customer.billing_email && (
                  <span className="flex items-center gap-1 truncate"><Mail className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{customer.billing_email}</span></span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirm === 'customer'} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Customer</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{customer.display_name}</strong> and all associated locations. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-customer">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCustomer}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-customer"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="customer-tabs">
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <TabsList className="inline-flex w-auto min-w-max">
              <TabsTrigger value="profile" data-testid="tab-profile">
                <User className="w-4 h-4 mr-1" /> Profile
              </TabsTrigger>
              <TabsTrigger value="jobs" data-testid="tab-jobs">
                <Briefcase className="w-4 h-4 mr-1" /> Jobs
              </TabsTrigger>
              <TabsTrigger value="estimates" data-testid="tab-estimates">
                <FileText className="w-4 h-4 mr-1" /> Est.
              </TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices">
                <Receipt className="w-4 h-4 mr-1" /> Inv.
              </TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">
                Activity
              </TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages">
                Msgs
              </TabsTrigger>
              <TabsTrigger value="calls" data-testid="tab-calls">
                Calls
              </TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">
                Notes
              </TabsTrigger>
              <TabsTrigger value="plans" data-testid="tab-plans">
                Plans
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6 mt-4">
            <ProfileTab
              customer={customer}
              canEdit={canEdit}
              openLocationDrawer={openLocationDrawer}
              handleDeleteLocation={handleDeleteLocation}
              handleDeleteCustomer={handleDeleteCustomer}
              deleteConfirm={deleteConfirm}
              setDeleteConfirm={setDeleteConfirm}
              onPropertyUpdate={(pp) => setCustomer(c => ({ ...c, property_profile: pp }))}
              onCustomerUpdate={(data) => setCustomer(data)}
            />
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="mt-4">
            <JobsTab jobs={jobs} loading={jobsLoading} navigate={navigate} />
          </TabsContent>

          {/* Estimates Tab */}
          <TabsContent value="estimates" className="mt-4">
            <CustomerEstimatesTab customerId={id} navigate={navigate} />
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-4">
            <CustomerInvoicesTab customerId={id} navigate={navigate} />
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-4">
            <CustomerActivityFeed customerId={id} />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-4">
            <CustomerMessagesTab
              customerId={id}
              customerName={customer?.display_name}
              customerPhones={{
                primary: customer?.primary_phone,
                secondary: customer?.secondary_phone,
                billing: customer?.billing_phone,
              }}
              selectedConversationId={searchParams.get('conv') || null}
              onSelectConversation={setSelectedConversationId}
            />
          </TabsContent>

          {/* Calls Tab */}
          <TabsContent value="calls" className="mt-4">
            <CustomerCallsTab customerId={id} />
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="mt-4">
            <CustomerNotesTabs
              customerId={id}
              customer={customer}
              onUpdate={(data) => setCustomer(data)}
            />
          </TabsContent>
          <TabsContent value="plans" className="mt-4">
            <CustomerMaintenancePlans customerId={id} />
          </TabsContent>
        </Tabs>

        {/* Edit Customer Drawer */}
        <AppDrawer
          open={editDrawerOpen}
          onOpenChange={setEditDrawerOpen}
          title="Edit Customer"
          description={`Update details for ${customer.display_name}`}
          data-testid="edit-customer-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setEditDrawerOpen(false)}>Cancel</Button>
              <Button type="submit" form="edit-customer-form" disabled={saving} data-testid="save-customer-edit">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </>
          }
        >
          <EditCustomerForm
            form={editForm}
            setForm={setEditForm}
            onSubmit={handleSaveCustomer}
            onPlaceSelect={handlePlaceSelect}
            allCustomerTags={allCustomerTags}
            onCreateTag={handleCreateTag}
            businessCountry={businessCountry}
            serviceCountries={serviceCountries}
            currentCustomerId={id}
          />
        </AppDrawer>

        {/* Location Drawer */}
        <AppDrawer
          open={locationDrawerOpen}
          onOpenChange={setLocationDrawerOpen}
          title={editingLocation ? 'Edit Location' : 'Add Location'}
          description={editingLocation ? 'Update the location details.' : 'Add a billing or service address for this customer.'}
          data-testid="location-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setLocationDrawerOpen(false)}>Cancel</Button>
              <Button type="submit" form="location-form" disabled={locationSaving} data-testid="save-location-btn">
                {locationSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingLocation ? 'Update' : 'Add'} Location
              </Button>
            </>
          }
        >
          <form id="location-form" onSubmit={handleSaveLocation} className="space-y-4">
            <div>
              <Label>Label</Label>
              <Input value={locationForm.label} onChange={(e) => setLocationForm({ ...locationForm, label: e.target.value })} placeholder="e.g. Main Office" data-testid="input-location-label" />
            </div>
            <div>
              <Label>{getAddressConfig(businessCountry).fields.street.label}</Label>
              <AddressAutocomplete
                value={locationForm.address1}
                onChange={(v) => setLocationForm({ ...locationForm, address1: v })}
                onPlaceSelect={handleLocationPlaceSelect}
                fieldPrefix=""
                placeholder={getAddressConfig(businessCountry).fields.street.placeholder}
                countryCode={getAddressConfig(businessCountry).regionCode}
                countryCodes={serviceCountries.map(c => c.toLowerCase())}
                data-testid="input-address1"
              />
            </div>
            <div>
              <Label>{getAddressConfig(businessCountry).fields.unit.label}</Label>
              <Input value={locationForm.address2} onChange={(e) => setLocationForm({ ...locationForm, address2: e.target.value })} placeholder={getAddressConfig(businessCountry).fields.unit.placeholder || 'Suite 100'} data-testid="input-address2" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{getAddressConfig(businessCountry).fields.city.label}</Label>
                <Input value={locationForm.city} onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })} data-testid="input-city" />
              </div>
              <div>
                <Label>{getAddressConfig(businessCountry).fields.state.label}</Label>
                <Input value={locationForm.state} onChange={(e) => setLocationForm({ ...locationForm, state: e.target.value })} data-testid="input-state" />
              </div>
              <div>
                <Label>{getAddressConfig(businessCountry).fields.zip.label}</Label>
                <Input value={locationForm.zip} onChange={(e) => setLocationForm({ ...locationForm, zip: e.target.value })} data-testid="input-zip" />
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={locationForm.is_billing_location} onCheckedChange={(v) => setLocationForm({ ...locationForm, is_billing_location: v })} data-testid="switch-billing" />
                <Label>Billing Address</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={locationForm.is_service_location} onCheckedChange={(v) => setLocationForm({ ...locationForm, is_service_location: v })} data-testid="switch-service" />
                <Label>Service Address</Label>
              </div>
            </div>
          </form>
        </AppDrawer>
      </div>
    </Layout>
  );
}

/* ---- Profile Tab ---- */
function ProfileTab({ customer, canEdit, openLocationDrawer, handleDeleteLocation, handleDeleteCustomer, deleteConfirm, setDeleteConfirm, onPropertyUpdate, onCustomerUpdate }) {
  const serviceAddress = [customer.service_street, customer.service_unit].filter(Boolean).join(', ');
  const serviceCityLine = [customer.service_city, customer.service_state].filter(Boolean).join(', ') + (customer.service_zip ? ` ${customer.service_zip}` : '');
  const formattedAddress = [serviceAddress, serviceCityLine].filter(s => s && s.trim()).join(', ');

  const [summary, setSummary] = useState(null);
  const [propertyDrawerOpen, setPropertyDrawerOpen] = useState(false);
  const [propertyForm, setPropertyForm] = useState({});
  const [propertySaving, setPropertySaving] = useState(false);

  useEffect(() => {
    customersAPI.getSummary(customer.id).then(res => setSummary(res.data)).catch(() => setSummary(null));
  }, [customer.id]);

  const profile = customer.property_profile;
  const hasPropertyData = profile && Object.entries(profile).some(
    ([k, v]) => !['id', 'customer_id', 'enrichment_source', 'enriched_at', 'created_at', 'updated_at'].includes(k) && v != null
  );

  const openPropertyDrawer = () => {
    setPropertyForm({
      property_type: profile?.property_type || '',
      square_footage: profile?.square_footage || '',
      year_built: profile?.year_built || '',
      lot_size_sqft: profile?.lot_size_sqft || '',
      bedrooms: profile?.bedrooms || '',
      bathrooms: profile?.bathrooms || '',
      estimated_value: profile?.estimated_value || '',
      last_sale_date: profile?.last_sale_date || '',
      last_sale_price: profile?.last_sale_price || '',
    });
    setPropertyDrawerOpen(true);
  };

  const handleSaveProperty = async (e) => {
    e.preventDefault();
    setPropertySaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(propertyForm)) {
        if (v === '' || v === null || v === undefined) continue;
        if (k === 'property_type' || k === 'last_sale_date') payload[k] = v;
        else if (k === 'bathrooms') payload[k] = parseFloat(v);
        else payload[k] = parseInt(v, 10);
      }
      const { data } = await customersAPI.upsertProperty(customer.id, payload);
      onPropertyUpdate(data);
      setPropertyDrawerOpen(false);
      toast.success('Property profile updated');
    } catch {
      toast.error('Failed to save property profile');
    } finally {
      setPropertySaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Contact Info + Service Address Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Contact Info */}
        <Card data-testid="contact-info-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Phone className="w-4 h-4" /> Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-0 items-start">
              {/* Left: contact fields + quick actions */}
              <div className="pr-5">
                <div className="space-y-3">
                  <ClickablePhoneRow label="Phone" value={customer.primary_phone} customerId={customer.id} customerName={customer.display_name} />
                  <ClickablePhoneRow label="Alt Phone" value={customer.secondary_phone} customerId={customer.id} customerName={customer.display_name} />
                  <InfoRow label="Email" value={customer.billing_email} />
                  <InfoRow label="Customer Type" value={customer.customer_type} className="capitalize" />
                  {customer.first_name && <InfoRow label="First Name" value={customer.first_name} />}
                  {customer.last_name && <InfoRow label="Last Name" value={customer.last_name} />}
                  {customer.company_name && <InfoRow label="Company" value={customer.company_name} />}
                  {customer.billing_customer && (
                    <BillingBadge billingCustomer={customer.billing_customer} className="mt-2" />
                  )}
                </div>

                {/* Quick Actions — under contact fields */}
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground/70 font-medium mb-2">Quick Actions</p>
                  <div className="flex items-center gap-1.5">
                    {customer.primary_phone && (
                      <button
                        onClick={() => window.dispatchEvent(new CustomEvent('softphone:dial', { detail: { number: customer.primary_phone, customerId: customer.id, customerName: customer.display_name } }))}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors"
                        title="Call"
                        data-testid="quick-action-call"
                      >
                        <Phone className="w-[18px] h-[18px]" />
                      </button>
                    )}
                    {customer.primary_phone && (
                      <button
                        onClick={() => window.open(`sms:${customer.primary_phone}`, '_self')}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                        title="Text / SMS"
                        data-testid="quick-action-sms"
                      >
                        <MessageSquare className="w-[18px] h-[18px]" />
                      </button>
                    )}
                    {customer.billing_email && (
                      <button
                        onClick={() => window.open(`mailto:${customer.billing_email}`, '_self')}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
                        title="Email"
                        data-testid="quick-action-email"
                      >
                        <Mail className="w-[18px] h-[18px]" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: customer summary only */}
              <div className="border-l pl-5">
                {summary && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground/70 font-medium mb-2.5">Customer Summary</p>
                    <div className="space-y-2">
                      <div className="flex items-baseline justify-between" data-testid="summary-lifetime-value">
                        <span className="text-sm text-muted-foreground">Lifetime Value</span>
                        <span className="text-sm font-semibold tabular-nums">${summary.lifetime_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex items-baseline justify-between" data-testid="summary-outstanding-balance">
                        <span className="text-sm text-muted-foreground">Outstanding</span>
                        <span className={`text-sm font-semibold tabular-nums ${summary.outstanding_balance > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>${summary.outstanding_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>

                      <div className="border-t my-1" />

                      <SummaryStatRow icon={Briefcase} label="Open Jobs" value={summary.open_jobs} testId="summary-open-jobs" />
                      <SummaryStatRow icon={FileText} label="Open Estimates" value={summary.open_estimates} testId="summary-open-estimates" />
                      <SummaryStatRow icon={Receipt} label="Unpaid Invoices" value={summary.unpaid_invoices} testId="summary-unpaid-invoices" />
                      {summary.best_plan_name ? (
                        <div className="flex items-center justify-between" data-testid="summary-plan-badge">
                          <span className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Shield className="w-3.5 h-3.5" /> Plan
                          </span>
                          <MaintenancePlanBadge planName={summary.best_plan_name} planStatus={summary.best_plan_status} size="sm" />
                        </div>
                      ) : (
                        <SummaryStatRow icon={Shield} label="Active Plans" value={summary.active_plans} testId="summary-active-plans" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Address + Property Details */}
        <Card data-testid="service-address-card" className="overflow-hidden">
          {customer.service_street ? (
            <div className="flex flex-col md:flex-row">
              {/* Left: address + property details + Google Maps link */}
              <div className="flex flex-col p-6 md:w-[45%] shrink-0">
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4" /> Service Address
                </p>
                <p className="text-sm">{serviceAddress}</p>
                {serviceCityLine.trim() && <p className="text-sm">{serviceCityLine}</p>}
                {customer.service_address_notes && (
                  <p className="text-xs text-muted-foreground italic mt-1">{customer.service_address_notes}</p>
                )}

                {/* Property Details — stacked below address */}
                <div className="mt-4 pt-3 border-t" data-testid="property-details-section">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Home className="w-4 h-4" /> Property Details
                    </p>
                    {canEdit && (
                      <button
                        onClick={openPropertyDrawer}
                        className="inline-flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors"
                        title={hasPropertyData ? 'Edit' : 'Add'}
                        data-testid="property-edit-btn"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {hasPropertyData ? (
                    <CompactPropertySummary profile={profile} />
                  ) : (
                    <p className="text-sm text-muted-foreground italic" data-testid="property-no-data">
                      No property data.{canEdit ? ' Click the pencil to add.' : ''}
                    </p>
                  )}
                </div>

                {buildGoogleMapsUrl(formattedAddress, customer.latitude, customer.longitude) && (
                  <Button
                    variant="ghost" size="sm"
                    className="mt-3 text-xs text-muted-foreground hover:text-primary h-7 w-fit px-2"
                    onClick={() => window.open(buildGoogleMapsUrl(formattedAddress, customer.latitude, customer.longitude), '_blank')}
                    data-testid="open-google-maps-btn"
                  >
                    <ExternalLink className="w-3 h-3 mr-1.5" /> Open in Google Maps
                  </Button>
                )}
              </div>

              {/* Right: map */}
              <div className="md:flex-1 min-h-[200px]">
                <CompactMapPreview
                  address={formattedAddress}
                  latitude={customer.latitude}
                  longitude={customer.longitude}
                />
              </div>
            </div>
          ) : (
            <>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Service Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground italic">No service address set.</p>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Communication Preferences + Additional Details + Attachments */}
      <div className="grid gap-6 md:grid-cols-3">
        <CommunicationPreferencesCard
          customerId={customer.id}
          customer={customer}
          onUpdate={onCustomerUpdate}
        />

        {/* Additional Details — Tags & Lead Source */}
        <Card data-testid="metadata-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Tag className="w-4 h-4" /> Additional Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Tags</p>
                {customer.tag_objects && customer.tag_objects.length > 0 ? (
                  <TagChips tags={customer.tag_objects} />
                ) : (
                  <p className="text-sm text-muted-foreground italic">None</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Lead Source</p>
                {(customer.lead_source_name || customer.lead_source) ? (
                  <Badge variant="secondary" className="text-xs font-medium" data-testid="lead-source-badge">
                    {customer.lead_source_name || customer.lead_source}
                  </Badge>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Not set</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attachments — moved into row 2 */}
        <CustomerAttachmentsCard customerId={customer.id} />
      </div>

      {/* Locations */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Locations ({customer.locations?.length || 0})
          </h2>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => openLocationDrawer()} data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Location
            </Button>
          )}
        </div>
        {(!customer.locations || customer.locations.length === 0) ? (
          <Card>
            <CardContent className="flex flex-col items-center py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-3">
                <MapPin className="w-5 h-5 text-muted-foreground/50" />
              </div>
              <p className="text-sm font-medium mb-1">No additional locations</p>
              <p className="text-xs text-muted-foreground max-w-[280px] mb-4">
                The service address above is the primary location for this customer.
              </p>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => openLocationDrawer()} data-testid="empty-add-location-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Location
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {customer.locations.map((loc) => (
              <Card key={loc.id} data-testid={`location-card-${loc.id}`}>
                <CardContent className="py-4 px-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{loc.label || 'Unnamed Location'}</span>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openLocationDrawer(loc)} data-testid={`edit-location-${loc.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        {deleteConfirm === loc.id ? (
                          <div className="flex items-center gap-1">
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteLocation(loc.id)} data-testid={`confirm-delete-loc-${loc.id}`}>Delete</Button>
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}><X className="w-3.5 h-3.5" /></Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(loc.id)} data-testid={`delete-location-${loc.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {[loc.address1, loc.address2].filter(Boolean).join(', ')}
                    {(loc.address1 || loc.address2) && (loc.city || loc.state || loc.zip) && <br />}
                    {[loc.city, loc.state].filter(Boolean).join(', ')} {loc.zip || ''}
                  </p>
                  <div className="flex gap-2">
                    {loc.is_billing_location && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Billing</Badge>
                    )}
                    {loc.is_service_location && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Service</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Property Details Drawer */}
      <AppDrawer
        open={propertyDrawerOpen}
        onClose={() => setPropertyDrawerOpen(false)}
        title={hasPropertyData ? 'Edit Property Details' : 'Add Property Details'}
      >
        <form onSubmit={handleSaveProperty} className="space-y-4 p-1">
          <div>
            <Label className="text-xs">Property Type</Label>
            <Select value={propertyForm.property_type} onValueChange={v => setPropertyForm(f => ({ ...f, property_type: v }))}>
              <SelectTrigger data-testid="property-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {[
                  { value: 'single_family', label: 'Single Family' },
                  { value: 'condo_townhome', label: 'Condo / Townhome' },
                  { value: 'multi_family', label: 'Multi-Family' },
                  { value: 'commercial', label: 'Commercial' },
                  { value: 'unknown', label: 'Unknown' },
                ].map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Square Footage</Label>
              <Input type="number" value={propertyForm.square_footage} onChange={e => setPropertyForm(f => ({ ...f, square_footage: e.target.value }))} data-testid="property-sqft-input" />
            </div>
            <div>
              <Label className="text-xs">Year Built</Label>
              <Input type="number" value={propertyForm.year_built} onChange={e => setPropertyForm(f => ({ ...f, year_built: e.target.value }))} data-testid="property-year-input" />
            </div>
            <div>
              <Label className="text-xs">Lot Size (sq ft)</Label>
              <Input type="number" value={propertyForm.lot_size_sqft} onChange={e => setPropertyForm(f => ({ ...f, lot_size_sqft: e.target.value }))} data-testid="property-lot-input" />
            </div>
            <div>
              <Label className="text-xs">Bedrooms</Label>
              <Input type="number" value={propertyForm.bedrooms} onChange={e => setPropertyForm(f => ({ ...f, bedrooms: e.target.value }))} data-testid="property-bed-input" />
            </div>
            <div>
              <Label className="text-xs">Bathrooms</Label>
              <Input type="number" step="0.5" value={propertyForm.bathrooms} onChange={e => setPropertyForm(f => ({ ...f, bathrooms: e.target.value }))} data-testid="property-bath-input" />
            </div>
            <div>
              <Label className="text-xs">Estimated Value ($)</Label>
              <Input type="number" value={propertyForm.estimated_value} onChange={e => setPropertyForm(f => ({ ...f, estimated_value: e.target.value }))} data-testid="property-value-input" />
            </div>
            <div>
              <Label className="text-xs">Last Sale Date</Label>
              <Input type="date" value={propertyForm.last_sale_date} onChange={e => setPropertyForm(f => ({ ...f, last_sale_date: e.target.value }))} data-testid="property-sale-date-input" />
            </div>
            <div>
              <Label className="text-xs">Last Sale Price ($)</Label>
              <Input type="number" value={propertyForm.last_sale_price} onChange={e => setPropertyForm(f => ({ ...f, last_sale_price: e.target.value }))} data-testid="property-sale-price-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPropertyDrawerOpen(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={propertySaving} data-testid="property-edit-save">
              <Save className="w-4 h-4 mr-1" /> {propertySaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </AppDrawer>
    </div>
  );
}

/* ---- Jobs Tab ---- */
function JobsTab({ jobs, loading, navigate }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (jobs.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <Briefcase className="w-10 h-10 text-muted-foreground/40 mb-3" />
          <h3 className="font-semibold mb-1">No jobs yet</h3>
          <p className="text-sm text-muted-foreground">Jobs linked to this customer will appear here.</p>
        </CardContent>
      </Card>
    );
  }

  const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <Card data-testid="jobs-list">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Job #</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Created</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Scheduled</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Address</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Description</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Amount</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Status</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Assigned</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(job => (
              <tr
                key={job.id}
                className="border-b last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors"
                onClick={() => navigate(`/jobs/${job.id}`)}
                data-testid={`job-row-${job.id}`}
              >
                <td className="px-4 py-2.5 font-medium whitespace-nowrap">{cleanJobNumber(job.job_number)}</td>
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmt(job.created_at)}</td>
                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmt(job.scheduled_date || job.scheduled_start)}</td>
                <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[200px]">{job.address || job.service_address || '—'}</td>
                <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[180px]">{job.description || job.job_name || '—'}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">{job.amount != null ? `$${Number(job.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}` : '—'}</td>
                <td className="px-4 py-2.5"><JobStatusBadge status={job.status} /></td>
                <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[120px]">{job.assigned_to || job.technician_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---- Placeholder Tab ---- */
function PlaceholderTab({ icon: Icon, title, description, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="flex flex-col items-center py-16 text-center">
        <Icon className="w-12 h-12 text-muted-foreground/30 mb-4" />
        <h3 className="text-lg font-semibold mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground max-w-sm">{description}</p>
      </CardContent>
    </Card>
  );
}

/* ---- Activity Tab ---- */

/* ---- Edit Form ---- */
function EditCustomerForm({ form, setForm, onSubmit, onPlaceSelect, allCustomerTags, onCreateTag, businessCountry = 'US', serviceCountries = null, currentCustomerId }) {
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const addrCfg = getAddressConfig(businessCountry);
  const [billingSearch, setBillingSearch] = React.useState('');
  const [billingResults, setBillingResults] = React.useState([]);
  const [billingSearching, setBillingSearching] = React.useState(false);
  const [billingName, setBillingName] = React.useState(form._billing_display_name || '');

  React.useEffect(() => {
    if (!billingSearch || billingSearch.length < 2) { setBillingResults([]); return; }
    const t = setTimeout(async () => {
      setBillingSearching(true);
      try {
        const res = await customersAPI.list({ search: billingSearch, page_size: 8 });
        setBillingResults((res.data?.customers || []).filter(c => c.id !== currentCustomerId));
      } catch { setBillingResults([]); }
      setBillingSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [billingSearch, currentCustomerId]);

  return (
    <form id="edit-customer-form" onSubmit={onSubmit} className="space-y-5">
      <div>
        <Label>Customer Type</Label>
        <Select value={form.customer_type} onValueChange={(v) => update('customer_type', v)}>
          <SelectTrigger data-testid="edit-customer-type"><SelectValue placeholder="Select type..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="residential">Residential</SelectItem>
            <SelectItem value="business">Business</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Name *</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First Name</Label>
            <Input value={form.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="edit-first-name" />
          </div>
          <div>
            <Label>Last Name</Label>
            <Input value={form.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="edit-last-name" />
          </div>
        </div>
        <div>
          <Label>Company Name</Label>
          <Input value={form.company_name} onChange={(e) => update('company_name', e.target.value)} data-testid="edit-company-name" />
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Contact</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Phone *</Label>
            <Input value={form.primary_phone} onChange={(e) => update('primary_phone', e.target.value)} data-testid="edit-primary-phone" />
          </div>
          <div>
            <Label>Alt Phone</Label>
            <Input value={form.secondary_phone} onChange={(e) => update('secondary_phone', e.target.value)} data-testid="edit-secondary-phone" />
          </div>
        </div>
        <div>
          <Label>Email</Label>
          <Input value={form.billing_email} onChange={(e) => update('billing_email', e.target.value)} data-testid="edit-billing-email" />
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Service Address *</p>
        <div>
          <Label>{addrCfg.fields.street.label} *</Label>
          <AddressAutocomplete
            value={form.service_street}
            onChange={(v) => update('service_street', v)}
            onPlaceSelect={onPlaceSelect}
            placeholder={addrCfg.fields.street.placeholder}
            countryCode={addrCfg.regionCode}
            countryCodes={(serviceCountries || []).map(c => c.toLowerCase())}
            data-testid="edit-service-street"
          />
        </div>
        <div>
          <Label>{addrCfg.fields.unit.label}</Label>
          <Input value={form.service_unit} onChange={(e) => update('service_unit', e.target.value)} data-testid="edit-service-unit" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>{addrCfg.fields.city.label}</Label><Input value={form.service_city} onChange={(e) => update('service_city', e.target.value)} data-testid="edit-service-city" /></div>
          <div><Label>{addrCfg.fields.state.label}</Label><Input value={form.service_state} onChange={(e) => update('service_state', e.target.value)} data-testid="edit-service-state" /></div>
          <div><Label>{addrCfg.fields.zip.label}</Label><Input value={form.service_zip} onChange={(e) => update('service_zip', e.target.value)} data-testid="edit-service-zip" /></div>
        </div>
        <div>
          <Label>Address Notes</Label>
          <Input value={form.service_address_notes} onChange={(e) => update('service_address_notes', e.target.value)} data-testid="edit-address-notes" />
        </div>
        {form.latitude && form.longitude && (
          <p className="text-xs text-muted-foreground">Coordinates: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}</p>
        )}
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Additional Details</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Tags</Label>
            <TagMultiSelect
              entityType="customer"
              selectedTags={form.selectedTags || []}
              onChange={(tags) => update('selectedTags', tags)}
              allTags={allCustomerTags}
              onCreateTag={onCreateTag}
              data-testid="edit-tags"
            />
          </div>
          <div>
            <Label>Lead Source</Label>
            <LeadSourceDropdown
              value={form.lead_source_id}
              valueName={form.lead_source_name}
              legacyValue={form.lead_source}
              onChange={(id, name) => { update('lead_source_id', id || ''); update('lead_source_name', name); }}
              data-testid="edit-lead-source"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={form.do_not_service} onCheckedChange={(v) => update('do_not_service', v)} data-testid="edit-do-not-service" />
          <Label className="flex items-center gap-1.5 text-destructive">
            <AlertTriangle className="w-4 h-4" /> Do Not Service
          </Label>
        </div>
      </div>

      {/* Bills To */}
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5">
          <Receipt className="w-3.5 h-3.5" /> Bills To
        </Label>
        {form.billing_customer_id ? (
          <div className="flex items-center gap-2 rounded-md border px-3 py-2 bg-amber-50/50 dark:bg-amber-950/20" data-testid="billing-customer-selected">
            <span className="text-sm font-medium flex-1">{billingName || 'Linked customer'}</span>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { update('billing_customer_id', ''); setBillingName(''); }} data-testid="clear-billing-customer">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
        ) : (
          <div className="relative">
            <Input
              placeholder="Search customer to bill to..."
              value={billingSearch}
              onChange={(e) => setBillingSearch(e.target.value)}
              data-testid="billing-customer-search"
            />
            {billingResults.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {billingResults.map(c => (
                  <button key={c.id} type="button" className="w-full text-left px-3 py-2 hover:bg-muted text-sm transition-colors" onClick={() => {
                    update('billing_customer_id', c.id);
                    setBillingName(c.display_name);
                    setBillingSearch('');
                    setBillingResults([]);
                  }} data-testid={`billing-option-${c.id}`}>
                    <span className="font-medium">{c.display_name}</span>
                    {c.billing_email && <span className="text-muted-foreground ml-2 text-xs">{c.billing_email}</span>}
                  </button>
                ))}
              </div>
            )}
            {billingSearching && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Optional. Set if invoices should be billed to a different entity.</p>
      </div>

      <div>
        <Label>Notes</Label>
        <Textarea value={form.customer_notes} onChange={(e) => update('customer_notes', e.target.value)} rows={3} data-testid="edit-customer-notes" />
      </div>
    </form>
  );
}

/* ---- Customer Estimates Tab ---- */
function CustomerEstimatesTab({ customerId, navigate }) {
  const [estimates, setEstimates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    estimatesAPI.list({ customer_id: customerId, page_size: 50 }).then(res => {
      setEstimates(res.data.estimates || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [customerId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (estimates.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <FileText className="w-10 h-10 text-muted-foreground/40 mb-3" />
          <h3 className="font-semibold mb-1">No estimates yet</h3>
          <p className="text-sm text-muted-foreground mb-3">Estimates for this customer will appear here.</p>
          <Button size="sm" onClick={() => navigate('/estimates')}><Plus className="w-4 h-4 mr-1" /> Create Estimate</Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="customer-estimates-list">
      {estimates.map(est => (
        <Card key={est.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate(`/estimates/${est.id}`)} data-testid={`customer-estimate-${est.id}`}>
          <CardContent className="flex items-center gap-4 py-3 px-5">
            <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm">{est.estimate_number}</span>
              <span className="text-xs text-muted-foreground ml-2">${est.total?.toFixed(2)}</span>
            </div>
            <Badge variant="outline" className="text-xs capitalize">{est.status?.replace(/_/g, ' ')}</Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CustomerInvoicesTab({ customerId, navigate }) {
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    invoicesAPI.list({ customer_id: customerId, page_size: 50 }).then(res => {
      setInvoices(res.data.invoices || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [customerId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (invoices.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <Receipt className="w-10 h-10 text-muted-foreground/40 mb-3" />
          <h3 className="font-semibold mb-1">No invoices yet</h3>
          <p className="text-sm text-muted-foreground mb-3">Invoices for this customer will appear here.</p>
          <Button size="sm" onClick={() => navigate('/invoices')}><Plus className="w-4 h-4 mr-1" /> Create Invoice</Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="customer-invoices-list">
      {invoices.map(inv => (
        <Card key={inv.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate(`/invoices/${inv.id}`)} data-testid={`customer-invoice-${inv.id}`}>
          <CardContent className="flex items-center gap-4 py-3 px-5">
            <Receipt className="w-5 h-5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm">{inv.invoice_number}</span>
              <span className="text-xs text-muted-foreground ml-2">${inv.total?.toFixed(2)}</span>
            </div>
            <Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---- Info Row ---- */
function InfoRow({ label, value, className = '' }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm ${className}`}>{value || <span className="text-muted-foreground italic">Not set</span>}</p>
    </div>
  );
}

/* ---- Summary Stat Row ---- */
function SummaryStatRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
      <span className={`text-sm font-semibold tabular-nums ${value > 0 ? 'text-foreground' : 'text-muted-foreground/40'}`}>{value}</span>
    </div>
  );
}

/* ---- Compact Property Summary ---- */
function CompactPropertySummary({ profile }) {
  const TYPES = {
    single_family: 'Single Family', condo_townhome: 'Condo / Townhome',
    multi_family: 'Multi-Family', commercial: 'Commercial', unknown: 'Unknown',
  };
  const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

  const pairs = [
    profile.bedrooms != null && ['Bed', profile.bedrooms],
    profile.bathrooms != null && ['Bath', profile.bathrooms],
    profile.square_footage && ['Sq Ft', Number(profile.square_footage).toLocaleString()],
    profile.estimated_value && ['Est. Value', fmtCurrency(profile.estimated_value)],
    profile.year_built && ['Year Built', profile.year_built],
    profile.property_type && ['Type', TYPES[profile.property_type] || profile.property_type],
    profile.lot_size_sqft && ['Lot', `${Number(profile.lot_size_sqft).toLocaleString()} sq ft`],
  ].filter(Boolean);

  if (pairs.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1" data-testid="compact-property-summary">
      {pairs.map(([label, value], i) => (
        <p key={i} className="text-sm">
          <span className="text-muted-foreground">{label}:</span>{' '}
          <span className="font-medium">{value}</span>
        </p>
      ))}
    </div>
  );
}

/* ---- Clickable Phone Row ---- */
function ClickablePhoneRow({ label, value, customerId, customerName }) {
  if (!value) {
    return (
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-muted-foreground italic">Not set</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <button
        onClick={() => {
          window.dispatchEvent(new CustomEvent('softphone:dial', {
            detail: { number: value, customerId, customerName }
          }));
        }}
        className="text-sm text-primary hover:underline cursor-pointer flex items-center gap-1"
        title="Click to call"
        data-testid={`click-to-call-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Phone className="w-3 h-3" />
        {value}
      </button>
    </div>
  );
}


/* ---- Maintenance Plan Status Badge ---- */
const PLAN_STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  expired: 'bg-gray-200 text-gray-700 dark:bg-gray-800/40 dark:text-gray-400',
  canceled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};
const PLAN_STATUS_LABELS = { active: 'Active', paused: 'Paused', expired: 'Expired', canceled: 'Canceled' };

function MaintenancePlanBadge({ planName, planStatus, size = 'default' }) {
  if (!planName || !planStatus) return null;
  const style = PLAN_STATUS_STYLES[planStatus] || PLAN_STATUS_STYLES.expired;
  const label = PLAN_STATUS_LABELS[planStatus] || planStatus;
  const sizeClass = size === 'sm' ? 'text-[10px] px-1.5 py-0' : 'text-xs px-2 py-0.5';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClass} ${style}`}
      data-testid="maintenance-plan-badge"
    >
      <Shield className="w-3 h-3" />
      <span className="truncate max-w-[120px]">{planName}</span>
      <span className="font-semibold">{label}</span>
    </span>
  );
}
